/**
 * Colonnes de la table Candidats d'une campagne : celles de la table des
 * parcours, plus une colonne par test.
 *
 * Une colonne de test montre le score global quand il est calculable (matching
 * au référentiel pour PAMI et PC Fit, couverture du barème pour FairView), et
 * sinon l'état de l'épreuve. Elle remplace la réglette de progression, qui
 * disait QUELLE épreuve restait à faire sans dire ce qu'avaient donné les
 * autres.
 *
 * Email, lien, progression et date de création restent disponibles dans le
 * menu « Colonnes », masqués par défaut : utiles pour relancer, secondaires
 * pour lire les résultats.
 */
import {
  PastilleScore,
  pastillePourcentage,
  TEINTES_PASTILLE,
  type Pastille,
  type TestCampagne,
} from "@repo/results";
import { StatutEvaluationBadge, type ColonneDataTable } from "@repo/ui";
import { interpreterCorrelation } from "../../../convex/referentiel/rapport";
import { colonnesParcours, type ParcoursLigne } from "./colonnesParcours";
import { LIBELLE_TEST } from "./ongletsCampagne";
import type { ScoresCandidats } from "./scoresCandidats";

/** L'épreuve d'un test dans le résumé d'un parcours. */
function epreuveDuTest(ligne: ParcoursLigne, test: TestCampagne) {
  return ligne.evaluationsResume.find((e) => {
    switch (test) {
      case "inbasket":
        return e.typeEval === "inbasket";
      case "entstruct":
        return e.typeEval === "fairview";
      case "pami":
        return e.typeEval === "form" && e.template === "pami";
      case "fitculturel":
        return e.typeEval === "form" && e.template === "pc-fit";
      case "copsoq3":
        return e.typeEval === "form" && e.template === "copsoq3";
    }
  });
}

/** Ce qu'une colonne de test mesure, en infobulle de l'en-tête. */
const AIDE_TEST: Partial<Record<TestCampagne, string>> = {
  pami: "Matching du profil PAMI aux fourchettes attendues pour le poste, sur 100.",
  fitculturel:
    "Verdict du PC Fit, tiré de la moyenne des corrélations entre le classement du candidat et celui de chaque référent (de −1, priorités inversées, à +1, accord ; le hasard vaut 0). Le tri suit cette moyenne.",
  entstruct:
    "Couverture du barème de l'entretien FairView, pondérée par le poids de chaque compétence, sur 100.",
};

/** Rang de l'état, pour trier sous les scores : terminé avant en cours. */
const RANG_STATUT: Record<string, number> = {
  termine: 4,
  analyse_en_cours: 3,
  soumis: 3,
  erreur_analyse: 2,
  en_cours: 1,
  non_commence: 0,
};

function scoreEtPastille(
  test: TestCampagne,
  ligne: ParcoursLigne,
  scores: ScoresCandidats,
): { score: number; pastille: Pastille } | null {
  if (test === "pami") {
    const s = scores.pami.get(ligne._id)?.scoreMatching ?? null;
    return s === null ? null : { score: s, pastille: pastillePourcentage(s) };
  }
  if (test === "entstruct") {
    const s = scores.entstruct.get(ligne._id) ?? null;
    return s === null ? null : { score: s, pastille: pastillePourcentage(s) };
  }
  if (test === "fitculturel") {
    const comparaison = scores.fitculturel.get(ligne._id);
    const r = comparaison?.scoreMatching ?? null;
    if (r === null) return null;
    // Le MOT du rapport, pas r : c'est lui qui se lit (arbitrage du
    // 30/10/2026, PR #84). Le tri, lui, suit r. Teinte par verdict :
    // « Éloigné » ne se confond pas avec « Pas de tendance nette ».
    const { sens, aLaLimite, libelle } = interpreterCorrelation(
      comparaison?.correlation ?? null,
    );
    return {
      score: r,
      pastille: {
        texte: libelle,
        classes:
          sens === "proche"
            ? aLaLimite
              ? TEINTES_PASTILLE.moyenne
              : TEINTES_PASTILLE.haute
            : sens === "eloigne"
              ? TEINTES_PASTILLE.alerte
              : TEINTES_PASTILLE.basse,
      },
    };
  }
  return null;
}

function colonneTest(
  test: TestCampagne,
  scores: ScoresCandidats,
): ColonneDataTable<ParcoursLigne> {
  return {
    id: `test-${test}`,
    header: () => <span title={AIDE_TEST[test]}>{LIBELLE_TEST[test]}</span>,
    // Score d'abord ; sans score, l'état de l'épreuve, toujours SOUS tout
    // score réel (r descend jusqu'à −1, d'où la marge).
    accessorFn: (l) => {
      const mesure = scoreEtPastille(test, l, scores);
      if (mesure) return mesure.score;
      const e = epreuveDuTest(l, test);
      return e ? -100 + (RANG_STATUT[e.statut] ?? 0) : -1000;
    },
    sortDescFirst: true,
    cell: ({ row }) => {
      const mesure = scoreEtPastille(test, row.original, scores);
      if (mesure) return <PastilleScore pastille={mesure.pastille} />;
      const e = epreuveDuTest(row.original, test);
      if (!e) return <span className="text-muted-foreground">—</span>;
      return <StatutEvaluationBadge statut={e.statut} />;
    },
    meta: { libelle: LIBELLE_TEST[test], largeur: "11%", auCentre: true },
  };
}

const MASQUEES_PAR_DEFAUT = new Set(["email", "lien", "progression", "dateCreation"]);

export function colonnesCandidats(
  tests: readonly TestCampagne[],
  scores: ScoresCandidats,
): ColonneDataTable<ParcoursLigne>[] {
  const base = colonnesParcours({ libelleAction: "Ouvrir" }).map((c) =>
    c.id && MASQUEES_PAR_DEFAUT.has(c.id)
      ? { ...c, meta: { ...c.meta, masqueeParDefaut: true } }
      : c,
  );
  const indexCandidat = base.findIndex((c) => c.id === "candidat");
  return [
    ...base.slice(0, indexCandidat + 1),
    ...tests.map((t) => colonneTest(t, scores)),
    ...base.slice(indexCandidat + 1),
  ];
}
